import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Upload,
  Download,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Trash2,
  RefreshCw,
  FileSpreadsheet,
  ArrowRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GrowwerSyncTab = () => {
  const { language } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const t = language === 'es' ? {
    title: 'Sincronización con Growwer',
    description: 'Compara tu catálogo de KeyPubli con los sitios de Growwer para detectar diferencias.',
    uploadTitle: 'Subir Excel de Growwer',
    uploadDesc: 'Sube el archivo Excel con los sitios actualmente en Growwer',
    expectedColumns: 'Columnas esperadas: URL/Dominio, Precio Regular, Precio Sensible, Categorías',
    selectFile: 'Seleccionar archivo Excel',
    uploading: 'Procesando...',
    results: 'Resultados de la comparación',
    growwerSites: 'Sitios en Growwer',
    keypubliSites: 'Sitios en KeyPubli',
    newForGrowwer: 'Nuevos para Growwer',
    priceChanges: 'Cambios de Precio',
    deletedInKeypubli: 'Eliminados en KeyPubli',
    downloadExcel: 'Descargar Excel con Resultados',
    downloadDesc: 'El Excel incluye 4 hojas: Nuevos, Cambios de Precio, Eliminados y Resumen',
    newForGrowwerDesc: 'Sitios en KeyPubli que no están en Growwer (con precios -10%)',
    priceChangesDesc: 'Sitios con precios diferentes entre KeyPubli y Growwer',
    deletedDesc: 'Sitios en Growwer que ya no existen en KeyPubli',
    instructions: '¿Cómo funciona?',
    step1: 'Exporta tu lista de sitios de Growwer a Excel',
    step2: 'Sube el archivo aquí',
    step3: 'El sistema compara con tu catálogo de KeyPubli',
    step4: 'Descarga el Excel con las diferencias detectadas',
    excelFeatures: 'El Excel de resultados incluye:',
    feature1: 'Precios de KeyPubli y Growwer (-10%)',
    feature2: 'Precios con redacción por Growwer (-10€) y por KeyPubli',
    feature3: 'Enlaces NO DoFollow marcados en rojo',
    feature4: 'Etiquetas de patrocinado (SI/YES/ASK) marcadas en rojo',
    feature5: 'Sitios eliminados con acción sugerida',
  } : {
    title: 'Growwer Synchronization',
    description: 'Compare your KeyPubli catalog with Growwer sites to detect differences.',
    uploadTitle: 'Upload Growwer Excel',
    uploadDesc: 'Upload the Excel file with sites currently in Growwer',
    expectedColumns: 'Expected columns: URL/Domain, Regular Price, Sensitive Price, Categories',
    selectFile: 'Select Excel file',
    uploading: 'Processing...',
    results: 'Comparison Results',
    growwerSites: 'Sites in Growwer',
    keypubliSites: 'Sites in KeyPubli',
    newForGrowwer: 'New for Growwer',
    priceChanges: 'Price Changes',
    deletedInKeypubli: 'Deleted in KeyPubli',
    downloadExcel: 'Download Excel with Results',
    downloadDesc: 'Excel includes 4 sheets: New, Price Changes, Deleted and Summary',
    newForGrowwerDesc: 'Sites in KeyPubli not in Growwer (with -10% prices)',
    priceChangesDesc: 'Sites with different prices between KeyPubli and Growwer',
    deletedDesc: 'Sites in Growwer that no longer exist in KeyPubli',
    instructions: 'How does it work?',
    step1: 'Export your Growwer site list to Excel',
    step2: 'Upload the file here',
    step3: 'System compares with your KeyPubli catalog',
    step4: 'Download Excel with detected differences',
    excelFeatures: 'Result Excel includes:',
    feature1: 'KeyPubli and Growwer prices (-10%)',
    feature2: 'Writer pricing by Growwer (-€10) and by KeyPubli',
    feature3: 'Non-DoFollow links marked in red',
    feature4: 'Sponsored labels (SI/YES/ASK) marked in red',
    feature5: 'Deleted sites with suggested action',
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/growwer/compare`, formData);
      setResults(response.data);
      toast.success(language === 'es' ? '¡Comparación completada!' : 'Comparison completed!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async () => {
    if (!results?.comparison_id) return;

    try {
      const response = await axios.get(`${API}/growwer/export/${results.comparison_id}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `growwer_sync_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(language === 'es' ? '¡Excel descargado!' : 'Excel downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(language === 'es' ? 'Error al descargar' : 'Download error');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <RefreshCw className="h-6 w-6" />
          {t.title}
        </h2>
        <p className="text-muted-foreground">{t.description}</p>
      </div>

      {/* Instructions */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileSpreadsheet className="h-5 w-5 text-blue-600" />
            </div>
            <div className="space-y-2 flex-1">
              <h4 className="font-medium text-blue-900">{t.instructions}</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>{t.step1}</li>
                <li>{t.step2}</li>
                <li>{t.step3}</li>
                <li>{t.step4}</li>
              </ol>
              <div className="mt-3 pt-3 border-t border-blue-200">
                <p className="text-sm font-medium text-blue-900">{t.excelFeatures}</p>
                <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside mt-1">
                  <li>{t.feature1}</li>
                  <li>{t.feature2}</li>
                  <li>{t.feature3}</li>
                  <li>{t.feature4}</li>
                  <li>{t.feature5}</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {t.uploadTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-2">{t.uploadDesc}</p>
          <p className="text-xs text-muted-foreground mb-4">{t.expectedColumns}</p>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleFileUpload}
            disabled={uploading}
          />
          
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full h-24 border-dashed border-2"
          >
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-8 w-8 mb-2 animate-spin" />
                <span>{t.uploading}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                <span>{t.selectFile}</span>
              </div>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            {t.results}
          </h3>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-slate-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{results.stats.growwer_sites}</p>
                <p className="text-xs text-muted-foreground">{t.growwerSites}</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-50">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{results.stats.keypubli_sites}</p>
                <p className="text-xs text-muted-foreground">{t.keypubliSites}</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{results.stats.new_for_growwer}</p>
                <p className="text-xs text-green-600">{t.newForGrowwer}</p>
              </CardContent>
            </Card>
            <Card className="bg-yellow-50 border-yellow-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{results.stats.price_changes}</p>
                <p className="text-xs text-yellow-600">{t.priceChanges}</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{results.stats.deleted_in_keypubli}</p>
                <p className="text-xs text-red-600">{t.deletedInKeypubli}</p>
              </CardContent>
            </Card>
          </div>

          {/* Explanation */}
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
              <ArrowRight className="h-4 w-4 text-green-600 mt-0.5" />
              <div>
                <p className="font-medium text-green-800">{t.newForGrowwer}</p>
                <p className="text-green-700">{t.newForGrowwerDesc}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-yellow-50 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">{t.priceChanges}</p>
                <p className="text-yellow-700">{t.priceChangesDesc}</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg">
              <Trash2 className="h-4 w-4 text-red-600 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">{t.deletedInKeypubli}</p>
                <p className="text-red-700">{t.deletedDesc}</p>
              </div>
            </div>
          </div>

          {/* Download Button */}
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t.downloadExcel}</p>
                  <p className="text-sm text-muted-foreground">{t.downloadDesc}</p>
                </div>
                <Button onClick={handleDownload} className="bg-green-600 hover:bg-green-700">
                  <Download className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Descargar' : 'Download'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GrowwerSyncTab;
